<template>
    <Headify :data="headMeta" />

    <div class="min-h-screen bg-white">
        <NavMain :key="route.name" />

        <div class="flex flex-1 flex-col lg:pl-64">
            <main class="flex-1">
                <div class="mx-auto w-full max-w-7xl px-2 pt-12 sm:px-4 lg:pt-4">
                    <div class="py-2">
                        <Breadcrumbs :links="breadcrumbsLinks" />
                        <BatchImportSteps :steps="wizzardSteps" />
                    </div>

                    <div
                        class="flex flex-col gap-2 text-center select-none sm:my-4 sm:flex-row sm:items-center sm:justify-center"
                    >
                        <ActionButton
                            :value="t('buttonDownloadJSONFile')"
                            state="info"
                            :is-disabled="canDownload === false"
                            :icon="MdiDownload"
                            @click="downloadJSONSchemaFile()"
                        />
                        <ActionButton
                            :value="t('buttonDownloadCSVFile')"
                            state="info"
                            :is-disabled="canDownload === false"
                            :icon="MdiDownload"
                            @click="downloadCSVSchemaFile()"
                        />
                        <ActionButton
                            :value="t('buttonContinue')"
                            state="primary"
                            :is-disabled="canDownload === false"
                            :icon="MdiChevronDoubleRight"
                            @click="
                                router.push({
                                    name: 'batch-import-4',
                                    params: {
                                        templateid: templateid,
                                        templateversionid: templateversionid,
                                    },
                                })
                            "
                        />
                    </div>
                </div>
            </main>
        </div>
    </div>
</template>
<script setup>
    import { reactive, ref } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import Headify from '../components/Headify.vue';
    import Breadcrumbs from '../ui/Breadcrumbs.vue';
    import NavMain from '../ui/NavMain.vue';
    import navs from '../composables/navs';
    import { t } from '../composables/i18n';
    import { uuidCreate } from '../composables/utils';
    import { getTemplate, getTemplateVersion } from '../composables/useTemplates';
    import {
        getTemplatePlaceholderFromRef,
        mapUsedPlaceholders,
        dumpCSV,
    } from '../composables/useTemplateSchema';
    import { harvestTemplate } from '../composables/useFreemarker';
    import BatchImportSteps from '../components/BatchImportSteps.vue';
    import ActionButton from '../ui/ActionButton.vue';
    import { MdiDownload, MdiChevronDoubleRight } from 'materialdesignicons-vue3/icons/';
    import slugify from 'slugify';

    const route = useRoute();
    const router = useRouter();
    const templateid = route.params.templateid;
    const templateversionid = route.params.templateversionid;
    const templateModel = reactive(await getTemplate(templateid));
    const templateVersionModel = reactive(await getTemplateVersion(templateversionid));
    const usedPlaceholdersExport = await harvestTemplate(templateVersionModel.source, []);
    const previewSampleData = reactive({
        templateCode: templateModel.code,
        langCode: templateVersionModel.langCode,
        version: templateVersionModel.version,
        duid: uuidCreate(),
        //performAction: ['PREVIEW'],
        valueCollection: {},
        recipient: {
            email: '',
        },
    });
    const canDownload = ref(false);

    const breadcrumbsLinks = [
        {
            name: 'dashboard',
            title: t('Dashboard'),
        },
        {
            title: t('BatchImport'),
            final: true,
        },
    ];

    const wizzardSteps = [
        {
            id: '01',
            title: t('Templates'),
            path: {
                name: 'batch-import-1',
                params: {},
            },
            status: 'complete',
        },
        {
            id: '02',
            title: t('TemplateVersions'),
            path: {
                name: 'batch-import-2',
                params: {
                    templateid: templateid,
                },
            },
            status: 'complete',
        },
        {
            id: '03',
            title: t('buttonSchema'),
            status: 'current',
        },
        {
            id: '04',
            title: t('processFiles'),
            status: 'upcoming',
        },
    ];

    const headMeta = navs().metaFind(route.name);

    const CSV = {
        fileName: `TEMPLATING-EXPORT-SCHEMA-${slugify(previewSampleData.templateCode)}.csv`,
        content: 'data;error',
    };

    const allPlaceholderGroups = await getTemplatePlaceholderFromRef(
        templateVersionModel.templateVersionId,
    );

    previewSampleData.valueCollection = mapUsedPlaceholders(
        usedPlaceholdersExport,
        allPlaceholderGroups,
    );

    CSV.content = dumpCSV(previewSampleData);
    canDownload.value = true;

    const downloadJSONSchemaFile = () => {
        const JSONData = [previewSampleData];
        delete JSONData[0].recipient;
        const blob = new Blob([JSON.stringify(JSONData, null, 4)], {
            type: 'application/json',
        });
        const linkSource = window.URL.createObjectURL(blob);
        const downloadLink = document.createElement('a');

        downloadLink.href = linkSource;
        downloadLink.download = `TEMPLATING-EXPORT-SCHEMA-${slugify(
            previewSampleData.templateCode,
        )}.json`;
        downloadLink.click();
    };

    const downloadCSVSchemaFile = () => {
        const blob = new Blob([CSV.content], { type: 'text/csv' });
        const linkSource = window.URL.createObjectURL(blob);
        const downloadLink = document.createElement('a');

        downloadLink.href = linkSource;
        downloadLink.download = CSV.fileName;
        downloadLink.click();
    };
</script>
